import { useEffect, useRef } from 'react';
import { getHolidaysInRange, preloadHolidays, type Holiday } from '../../../services/todo/holidays';

/**
 * 仅依赖当前月份加载节假日，与待办数据无关。
 * effect 依赖 [currentDate, setHolidays]：只有换月或 setHolidays 引用变化才会跑；
 * 修改待办不会改 currentDate，setHolidays 来自 useState 稳定，故不会因待办刷新节假日。
 */
export const useHolidays = (
  currentDate: Date,
  setHolidays: (holidays: Map<string, Holiday>) => void
) => {
  const prevMonthKeyRef = useRef<string | null>(null);

  useEffect(() => {
    preloadHolidays();
  }, []);

  useEffect(() => {
    const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
    const monthChanged = prevMonthKeyRef.current !== monthKey;
    if (monthChanged) {
      prevMonthKeyRef.current = monthKey;
      setHolidays(new Map());
    }

    const loadHolidays = async () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);

      try {
        const holidaysList = await getHolidaysInRange(startDate, endDate);
        const holidaysMap = new Map<string, Holiday>();
        holidaysList.forEach(holiday => {
          holidaysMap.set(holiday.date, holiday);
        });
        setHolidays(holidaysMap);
      } catch (error) {
        console.error('Failed to load holidays:', error);
      }
    };

    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => {
        loadHolidays();
      }, { timeout: 1000 });
    } else {
      setTimeout(() => {
        loadHolidays();
      }, 100);
    }
  }, [currentDate, setHolidays]);
};
